'use client';

import { createMachine, assign } from 'xstate';
// import type { State } from 'xstate'; // Removed due to XState version compatibility
import type { CartSaved } from '@/types/manage.types';

/**
 * Cart state machine context
 */
export interface CartContext {
  items: CartSaved[];
  lastError: string | null;
  syncInProgress: boolean;
  isDirty: boolean;
  lastSyncTimestamp: number | null;
  optimisticOperations: Array<{
    id: string;
    type: 'add' | 'update' | 'remove';
    data: unknown;
    timestamp: number;
  }>;
}

/**
 * Cart management events
 */
export type CartEvent =
  | {
      type: 'ADD_ITEM';
      item: Omit<CartSaved, 'in_cart'> & {
        note?: string;
        price?: number;
        attributes?: Array<{ key: string; value: string; label: string }>;
      };
    }
  | { type: 'UPDATE_ITEM'; variant_id: string; updates: Partial<CartSaved> }
  | { type: 'REMOVE_ITEM'; variant_id: string }
  | { type: 'CLEAR_CART' }
  | { type: 'LOAD_FROM_STORAGE'; items?: CartSaved[] }
  | { type: 'SYNC_TO_STORAGE' }
  | { type: 'SYNC_SUCCESS' }
  | { type: 'SYNC_FAILED'; error: string }
  | { type: 'UNDO_OPTIMISTIC'; operationId: string }
  | { type: 'COMMIT_OPTIMISTIC'; operationId: string }
  | { type: 'RETRY_SYNC' }
  | { type: 'NETWORK_ONLINE' }
  | { type: 'NETWORK_OFFLINE' };

/**
 * Cart state machine states
 */
export type CartState = 'idle' | 'loading' | 'syncing' | 'syncFailed' | 'offline' | 'optimistic';

/**
 * Cart management state machine
 * Handles cart operations with optimistic updates, offline support, and automatic syncing
 */
export const cartStateMachine = createMachine(
  {
    /** @xstate-layout N4IgpgJg5mDOIC5QAoC2BDAxgCwJYDswBKAOlwgBswBiAFQEkBhAQVwBUBRAOQGUBBAHIBJAJIARAMIApANoAGALqJQABwD2sVLQJyQAD0QBGAKwA2EgE4ALLoBsAdgCcAFgBMAGhABPRJdIkGiQaJFoSJxp6AC8PbwBfGJ80TBx8IjJKGjpGZiZ2Tl5+WVppOQVlNW0EMJJaEicSJx86Zw8vbxIcmOjMXAJSCipaMgZ7Zk5uXn5hCUUpOVL1bQQnOibnOmrXBo8-P18E5NT0LBwpvPnF5dKhYSqFGuNmzxI6Gg8nOicaNw9fJJgiIRKJMGFxgJDrEjkMzFN6HV8uV5G8nMJHiR6NRaBxeLx+KQhHJBKI5JJ5HIFKp1GVlAI4c5ap54Z1EXC0ci4qjrtdxqiMZMMSJHjQsZZOCSeGytoVyQoyaTKYyaYgUP8AkCsnC2Y1OZiPAiNOp1FAjJpfIhaFRHHQjfRjWzIBzeUr6Eiq+r1byIYgSKxvb6eY0Hbj5WbFqRrXarbz7fzHX8HYgFLVaOcbqoHKxXApfJpYiQvIkgA */
    id: 'cartManagement',
    initial: 'loading',

    context: {
      items: [],
      lastError: null,
      syncInProgress: false,
      isDirty: false,
      lastSyncTimestamp: null,
      optimisticOperations: [],
    } as CartContext,

    states: {
      loading: {
        entry: 'loadFromStorage',
        on: {
          LOAD_FROM_STORAGE: {
            target: 'idle',
            actions: 'setItemsFromStorage',
          },
        },
      },

      idle: {
        on: {
          ADD_ITEM: {
            target: 'optimistic',
            actions: ['addItemOptimistic', 'markDirty'],
          },
          UPDATE_ITEM: {
            target: 'optimistic',
            actions: ['updateItemOptimistic', 'markDirty'],
          },
          REMOVE_ITEM: {
            target: 'optimistic',
            actions: ['removeItemOptimistic', 'markDirty'],
          },
          CLEAR_CART: {
            target: 'optimistic',
            actions: ['clearCartOptimistic', 'markDirty'],
          },
          SYNC_TO_STORAGE: {
            target: 'syncing',
            guard: 'isDirty',
          },
          NETWORK_OFFLINE: 'offline',
        },
      },

      optimistic: {
        after: {
          1000: {
            // Auto-sync after 1 second of inactivity
            target: 'syncing',
            guard: 'isDirty',
          },
        },
        on: {
          ADD_ITEM: {
            actions: ['addItemOptimistic', 'markDirty'],
          },
          UPDATE_ITEM: {
            actions: ['updateItemOptimistic', 'markDirty'],
          },
          REMOVE_ITEM: {
            actions: ['removeItemOptimistic', 'markDirty'],
          },
          CLEAR_CART: {
            actions: ['clearCartOptimistic', 'markDirty'],
          },
          SYNC_TO_STORAGE: {
            target: 'syncing',
            guard: 'isDirty',
          },
          UNDO_OPTIMISTIC: {
            actions: 'undoOptimisticOperation',
          },
          NETWORK_OFFLINE: 'offline',
        },
      },

      syncing: {
        entry: ['syncToStorage', 'setSyncInProgress'],
        on: {
          SYNC_SUCCESS: {
            target: 'idle',
            actions: ['clearSyncInProgress', 'updateLastSyncTimestamp', 'commitOptimisticOperations'],
          },
          SYNC_FAILED: {
            target: 'syncFailed',
            actions: ['clearSyncInProgress', 'setError'],
          },
          // Allow optimistic updates during sync
          ADD_ITEM: {
            actions: ['addItemOptimistic', 'markDirty'],
          },
          UPDATE_ITEM: {
            actions: ['updateItemOptimistic', 'markDirty'],
          },
          REMOVE_ITEM: {
            actions: ['removeItemOptimistic', 'markDirty'],
          },
        },
      },

      syncFailed: {
        on: {
          RETRY_SYNC: {
            target: 'syncing',
            actions: 'clearError',
          },
          ADD_ITEM: {
            target: 'optimistic',
            actions: ['addItemOptimistic', 'markDirty', 'clearError'],
          },
          UPDATE_ITEM: {
            target: 'optimistic',
            actions: ['updateItemOptimistic', 'markDirty', 'clearError'],
          },
          REMOVE_ITEM: {
            target: 'optimistic',
            actions: ['removeItemOptimistic', 'markDirty', 'clearError'],
          },
          NETWORK_OFFLINE: 'offline',
        },
      },

      offline: {
        on: {
          ADD_ITEM: {
            actions: ['addItemOptimistic', 'markDirty'],
          },
          UPDATE_ITEM: {
            actions: ['updateItemOptimistic', 'markDirty'],
          },
          REMOVE_ITEM: {
            actions: ['removeItemOptimistic', 'markDirty'],
          },
          CLEAR_CART: {
            actions: ['clearCartOptimistic', 'markDirty'],
          },
          NETWORK_ONLINE: [
            {
              target: 'syncing',
              guard: 'isDirty',
            },
            {
              target: 'idle',
            },
          ],
        },
      },
    },
  },
  {
    guards: {
      isDirty: ({ context }) => context.isDirty,
    },

    actions: {
      loadFromStorage: () => {
        console.log('Loading cart from storage');
      },

      setItemsFromStorage: assign({
        items: ({ event }) => {
          if (event.type === 'LOAD_FROM_STORAGE') {
            return event.items || [];
          }
          return [];
        },
        lastSyncTimestamp: Date.now(),
      }),

      addItemOptimistic: assign({
        items: ({ context, event }) => {
          if (event.type !== 'ADD_ITEM') return context.items;

          const existingIndex = context.items.findIndex((item) => item.variant_id === event.item.variant_id);

          const newItem: CartSaved = {
            ...event.item,
            in_cart: {
              added_at: Date.now(),
              price: event.item.price || 0,
              selected_attributes: event.item.attributes || [],
            },
          };

          if (existingIndex >= 0) {
            // Update existing item quantity
            const updatedItems = [...context.items];
            updatedItems[existingIndex] = {
              ...updatedItems[existingIndex],
              quantity: (updatedItems[existingIndex].quantity || 1) + (event.item.quantity || 1),
            };
            return updatedItems;
          } else {
            // Add new item
            return [...context.items, newItem];
          }
        },
        optimisticOperations: ({ context, event }) => {
          if (event.type !== 'ADD_ITEM') return context.optimisticOperations;

          const operationId = `add_${Date.now()}_${Math.random()}`;
          return [
            ...context.optimisticOperations,
            {
              id: operationId,
              type: 'add' as const,
              data: event.item,
              timestamp: Date.now(),
            },
          ];
        },
      }),

      updateItemOptimistic: assign({
        items: ({ context, event }) => {
          if (event.type !== 'UPDATE_ITEM') return context.items;

          return context.items.map((item) =>
            item.variant_id === event.variant_id ? { ...item, ...event.updates } : item,
          );
        },
        optimisticOperations: ({ context, event }) => {
          if (event.type !== 'UPDATE_ITEM') return context.optimisticOperations;

          const operationId = `update_${Date.now()}_${Math.random()}`;
          return [
            ...context.optimisticOperations,
            {
              id: operationId,
              type: 'update' as const,
              data: { variant_id: event.variant_id, updates: event.updates },
              timestamp: Date.now(),
            },
          ];
        },
      }),

      removeItemOptimistic: assign({
        items: ({ context, event }) => {
          if (event.type !== 'REMOVE_ITEM') return context.items;

          return context.items.filter((item) => item.variant_id !== event.variant_id);
        },
        optimisticOperations: ({ context, event }) => {
          if (event.type !== 'REMOVE_ITEM') return context.optimisticOperations;

          const operationId = `remove_${Date.now()}_${Math.random()}`;
          return [
            ...context.optimisticOperations,
            {
              id: operationId,
              type: 'remove' as const,
              data: { variant_id: event.variant_id },
              timestamp: Date.now(),
            },
          ];
        },
      }),

      clearCartOptimistic: assign({
        items: [],
        optimisticOperations: ({ context }) => {
          const operationId = `clear_${Date.now()}_${Math.random()}`;
          return [
            ...context.optimisticOperations,
            {
              id: operationId,
              type: 'remove' as const,
              data: { clear: true },
              timestamp: Date.now(),
            },
          ];
        },
      }),

      undoOptimisticOperation: assign({
        optimisticOperations: ({ context, event }) => {
          if (event.type !== 'UNDO_OPTIMISTIC') return context.optimisticOperations;

          return context.optimisticOperations.filter((op) => op.id !== event.operationId);
        },
        // Note: Actual item rollback would need more complex logic
      }),

      commitOptimisticOperations: assign({
        optimisticOperations: [],
      }),

      markDirty: assign({
        isDirty: true,
      }),

      syncToStorage: () => {
        console.log('Syncing cart to storage');
      },

      setSyncInProgress: assign({
        syncInProgress: true,
      }),

      clearSyncInProgress: assign({
        syncInProgress: false,
        isDirty: false,
      }),

      updateLastSyncTimestamp: assign({
        lastSyncTimestamp: Date.now(),
      }),

      setError: assign({
        lastError: ({ event }) => {
          if (event.type === 'SYNC_FAILED') {
            return event.error;
          }
          return null;
        },
      }),

      clearError: assign({
        lastError: null,
      }),
    },
  },
);

/**
 * Cart item helpers
 */
export const cartHelpers = {
  calculateTotal: (items: CartSaved[]): number => {
    return items.reduce((total, item) => {
      const price = item.in_cart?.price || 0;
      const quantity = item.quantity || 1;
      return total + price * quantity;
    }, 0);
  },

  getItemCount: (items: CartSaved[]): number => {
    return items.reduce((count, item) => count + (item.quantity || 1), 0);
  },

  findItemByVariantId: (items: CartSaved[], variant_id: string): CartSaved | undefined => {
    return items.find((item) => item.variant_id === variant_id);
  },

  validateCartItems: (items: CartSaved[]): { isValid: boolean; errors: string[] } => {
    const errors: string[] = [];

    if (items.length === 0) {
      errors.push('Cart is empty');
    }

    items.forEach((item, index) => {
      if (!item.variant_id) {
        errors.push(`Item ${index + 1}: Variant ID is required`);
      }
      if (!item.quantity || item.quantity <= 0) {
        errors.push(`Item ${index + 1}: Valid quantity is required`);
      }
    });

    return {
      isValid: errors.length === 0,
      errors,
    };
  },
};

/**
 * Cart machine state type
 */
type CartMachineState = { context: CartContext; matches: (state: string) => boolean };

/**
 * Cart state selectors
 */
export const cartSelectors = {
  isLoading: (state: CartMachineState) => state.matches('loading'),
  isIdle: (state: CartMachineState) => state.matches('idle'),
  isSyncing: (state: CartMachineState) => state.matches('syncing'),
  isSyncFailed: (state: CartMachineState) => state.matches('syncFailed'),
  isOffline: (state: CartMachineState) => state.matches('offline'),
  isOptimistic: (state: CartMachineState) => state.matches('optimistic'),

  hasItems: (state: CartMachineState) => state.context.items.length > 0,
  isEmpty: (state: CartMachineState) => state.context.items.length === 0,
  isDirty: (state: CartMachineState) => state.context.isDirty,
  hasError: (state: CartMachineState) => Boolean(state.context.lastError),
  hasPendingOperations: (state: CartMachineState) => state.context.optimisticOperations.length > 0,

  canSync: (state: CartMachineState) => state.context.isDirty && !state.matches('syncing'),
  canRetry: (state: CartMachineState) => state.matches('syncFailed'),

  getItemCount: (state: CartMachineState) => cartHelpers.getItemCount(state.context.items),
  getTotal: (state: CartMachineState) => cartHelpers.calculateTotal(state.context.items),
};
